import { useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../Icon';
import styles from './RowMenu.module.css';

/**
 * One entry of a row's ⋯ menu: an action, or a place that opens in its own tab.
 * `separated` draws a rule above the entry, the way a destructive last entry stands apart.
 */
export interface RowMenuEntry {
  key: string;
  label: string;
  onAct?: () => void;
  /** A place the entry opens in a new tab, such as the uploaded file through the File Access Proxy. */
  href?: string;
  separated?: boolean;
}

/**
 * 🔒 A row's ⋯ menu (T3082; decision 7440): one borderless icon button that opens a short list of the row's acts.
 * The list is drawn in the page's top layer, placed under the button, so a tile's own scrolling never clips it, and
 * it closes on a press outside it, on Escape, on a choice, and when the page scrolls or resizes under it.
 */
export function RowMenu({ label, entries }: { label: string; entries: readonly RowMenuEntry[] }) {
  const [open, setOpen] = useState(false);
  const [at, setAt] = useState<{ top: number; right: number; layer: HTMLElement } | null>(null);
  const button = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const listId = useId();

  useLayoutEffect(() => {
    if (!open) return;
    const rect = button.current?.getBoundingClientRect();
    // 🔨 The list goes into the open dialog where the button is in one, as the Button's panel does: a layer on the body is painted behind a modal.
    if (rect) setAt({ top: rect.bottom, right: window.innerWidth - rect.right, layer: button.current?.closest('dialog') ?? document.body });
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const outside = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!(button.current?.contains(target) ?? false) && !(list.current?.contains(target) ?? false)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      button.current?.focus();
    };
    const close = () => setOpen(false);
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', escape);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('keydown', escape);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  // 🔒 Opening moves focus to the first entry, so a keyboard user can use what they opened (T3082 review fix).
  useEffect(() => {
    if (open && at) list.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
  }, [open, at]);

  /** Arrow keys, Home and End move between the entries; Tab closes the menu and returns to the trigger, whose next Tab goes on from there. */
  const onListKey = (event: ReactKeyboardEvent<HTMLUListElement>) => {
    const items = [...(list.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])];
    const current = items.indexOf(document.activeElement as HTMLElement);
    const go = (to: number) => {
      event.preventDefault();
      items[(to + items.length) % items.length]?.focus();
    };
    if (event.key === 'ArrowDown') go(current + 1);
    else if (event.key === 'ArrowUp') go(current < 0 ? items.length - 1 : current - 1);
    else if (event.key === 'Home') go(0);
    else if (event.key === 'End') go(items.length - 1);
    else if (event.key === 'Tab') {
      event.preventDefault();
      setOpen(false);
      button.current?.focus();
    }
  };

  if (entries.length === 0) return null;
  const menu = open && at ? (
    <ul ref={list} id={listId} className={styles.menu} role="menu" style={{ top: at.top, right: at.right }} data-row-menu="open" onKeyDown={onListKey}>
      {entries.map((entry) => (
        <li key={entry.key} role="none" className={entry.separated ? styles.separated : undefined}>
          {entry.href ? (
            <a role="menuitem" tabIndex={-1} className={styles.entry} href={entry.href} target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)} data-row-menu-entry={entry.key}>
              {entry.label}
            </a>
          ) : (
            <button type="button" role="menuitem" tabIndex={-1} className={styles.entry} onClick={() => { setOpen(false); entry.onAct?.(); }} data-row-menu-entry={entry.key}>
              {entry.label}
            </button>
          )}
        </li>
      ))}
    </ul>
  ) : null;

  return (
    <>
      <button ref={button} type="button" className={styles.trigger} aria-label={label} title={label} aria-haspopup="menu" aria-expanded={open} aria-controls={open ? listId : undefined} onClick={() => setOpen((held) => !held)} onKeyDown={(event) => { if (event.key === 'ArrowDown') { event.preventDefault(); setOpen(true); } }} data-row-menu-button="true">
        <Icon meaning="moreActions" size="inline" />
      </button>
      {menu ? createPortal(menu, at!.layer) : null}
    </>
  );
}
